import { ConfirmationModalFooter } from '@/components/ConfirmationModal/Base/Footer';
import { useApproveReport } from '@/features/approving_invoice_reports/components/ConfirmApproveModal/hooks/useApproveReport';
import { useBulkApproveReports } from '@/features/approving_invoice_reports/components/ConfirmApproveModal/hooks/useBulkApproveReport';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import { ButtonV2Props, Modal } from '@moneyforward/ap-frontend-components';
import { FC, memo, useCallback, useMemo } from 'react';
import {
  ApproveType,
  useConfirmApproveModal,
} from './context/ConfirmApproveModalContext';
import { ConfirmApproveModalProps } from './types';

type InnerConfirmApproveModalProps = {
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
};

export const InnerConfirmApproveModal: FC<InnerConfirmApproveModalProps> = memo(
  ({ onDrawerOpen, onDrawerClose }) => {
    const { open, onCloseConfirmApproveModal, reportTarget, approveType } =
      useConfirmApproveModal();
    const containerRef = useGlobalContainerRef();
    const { approveReport } = useApproveReport({
      onDrawerOpen,
      onDrawerClose,
    });

    const onBulkApproveSuccess = useCallback(() => {
      onDrawerClose();
    }, [onDrawerClose]);

    const { bulkApproveReports } = useBulkApproveReports({
      onSuccess: onBulkApproveSuccess,
    });
    const { t } = useTranslation();

    const handleApprove = useMemo(() => {
      // Multiple approve
      if (approveType === ApproveType.MULTIPLE && Array.isArray(reportTarget)) {
        return bulkApproveReports(reportTarget);
      }
      // Single approve
      if (
        approveType === ApproveType.SINGLE &&
        typeof reportTarget === 'string'
      ) {
        return approveReport(reportTarget);
      }
    }, [approveType, approveReport, bulkApproveReports, reportTarget]);

    const modalTitle = useMemo(() => {
      if (approveType === ApproveType.MULTIPLE && reportTarget?.length > 0) {
        return t('bulk_approve_invoice_reports_confirm_title', {
          count: reportTarget.length,
        });
      }
      return t('approve_invoice_report_confirm_title');
    }, [approveType, reportTarget, t]);

    const modalContent = useMemo(() => {
      if (approveType === ApproveType.MULTIPLE && reportTarget?.length > 0) {
        return t('bulk_approve_invoice_reports_confirm_message', {
          count: reportTarget.length,
        });
      }
      return t('approve_invoice_report_confirm_message');
    }, [approveType, reportTarget, t]);

    const cancelButtonProps: ButtonV2Props = useMemo(
      () => ({
        color: 'primary',
        isSecondary: true,
        onClick: onCloseConfirmApproveModal,
        size: 'sm',
        children: t('cancel'),
      }),
      [onCloseConfirmApproveModal, t]
    );
    const okButtonProps: ButtonV2Props = useMemo(
      () => ({
        color: 'primary',
        onClick: handleApprove,
        size: 'sm',
        isSecondary: false,
        children: t('approve_invoice_report_confirm_button'),
      }),
      [handleApprove, t]
    );

    return (
      <Modal
        open={open}
        onClose={onCloseConfirmApproveModal}
        size='sm'
        title={modalTitle}
        getContainer={containerRef}
        mask
        footer={
          <ConfirmationModalFooter
            cancelButton={cancelButtonProps}
            okButton={okButtonProps}
          />
        }
      >
        <div>{modalContent}</div>
      </Modal>
    );
  }
);

export const ConfirmApproveModal: FC<ConfirmApproveModalProps> = memo(
  ({ onDrawerOpen, onDrawerClose }) => {
    return (
      <InnerConfirmApproveModal
        onDrawerOpen={onDrawerOpen}
        onDrawerClose={onDrawerClose}
      />
    );
  }
);

InnerConfirmApproveModal.displayName = 'InnerConfirmApproveModal';
ConfirmApproveModal.displayName = 'ConfirmApproveModal';
